import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  CheckIcon,
  CalculatorIcon,
  StarIcon,
  ShieldCheckIcon,
  BoltIcon,
  UsersIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../components/LoadingSpinner";

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [accountCount, setAccountCount] = useState(5);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Pricing configuration matching backend limits
  const tiers = {
    free: {
      name: "Free",
      description: "Perfect for getting started",
      price: 0,
      accountLimit: 3,
      dataRetention: 30,
      features: [
        "Up to 3 social accounts",
        "Basic analytics dashboard",
        "30-day data retention",
        "Manual data refresh",
        "Email support",
      ],
      popular: false,
      buttonText: "Get Started Free",
    },
    standard: {
      name: "Standard",
      description: "For growing businesses",
      price: null, // Calculated dynamically
      accountLimit: 10,
      dataRetention: 90,
      features: [
        "Up to 10 social accounts",
        "Advanced analytics & insights",
        "90-day data retention",
        "Automatic data syncing",
        "Daily automated reports",
        "Priority email support",
        "Custom dashboards",
      ],
      popular: true,
      buttonText: "Start Free Trial",
    },
    premium: {
      name: "Premium",
      description: "For enterprise needs",
      price: null, // Calculated dynamically
      accountLimit: 50,
      dataRetention: 365,
      features: [
        "Up to 50 social accounts",
        "Everything in Standard",
        "365-day data retention",
        "Custom reports & exports",
        "API access for integrations",
        "Real-time monitoring",
        "Priority support",
        "Advanced integrations",
      ],
      popular: false,
      buttonText: "Contact Sales",
    },
  };

  const calculatePrice = (accountCount, tier) => {
    if (tier === "free") return 0;

    let basePrice;
    if (accountCount >= 10) {
      basePrice = accountCount * 10;
    } else if (accountCount >= 5) {
      basePrice = accountCount * 15;
    } else {
      basePrice = accountCount * 25;
    }

    // Premium tier adds 30%
    if (tier === "premium") {
      basePrice *= 1.3;
    }

    return Math.round(basePrice * 100) / 100;
  };

  const calculateAnnualPrice = (monthlyPrice) => {
    return Math.round(monthlyPrice * 12 * 0.9 * 100) / 100; // 10% discount
  };

  const getPriceBreakpoint = (accountCount) => {
    if (accountCount >= 10)
      return { accounts: 10, price: 10, description: "10+ accounts" };
    if (accountCount >= 5)
      return { accounts: 5, price: 15, description: "5-9 accounts" };
    return { accounts: 1, price: 25, description: "1-4 accounts" };
  };

  const handleGetStarted = (tier) => {
    if (tier === "free") {
      if (user) {
        navigate("/dashboard");
      } else {
        navigate("/register");
      }
    } else {
      navigate("/subscribe", {
        state: {
          tier,
          accountCount: Math.max(1, accountCount),
          billingCycle,
        },
      });
    }
  };

  useEffect(() => {
    // Update tier prices when account count changes
    const updatedTiers = { ...tiers };
    updatedTiers.standard.price = calculatePrice(accountCount, "standard");
    updatedTiers.premium.price = calculatePrice(accountCount, "premium");
    setPricingData(updatedTiers);
    setLoading(false);
  }, [accountCount]);

  const breakpoint = getPriceBreakpoint(accountCount);
  const standardMonthly = calculatePrice(accountCount, "standard");
  const premiumMonthly = calculatePrice(accountCount, "premium");
  const standardAnnual = calculateAnnualPrice(standardMonthly);
  const premiumAnnual = calculateAnnualPrice(premiumMonthly);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Hero Section */}
      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Simple, Transparent
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Pricing
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Choose the perfect plan for your social media analytics needs.
              Scale as you grow with volume discounts and flexible billing.
            </p>
          </div>
        </div>
      </div>

      {/* Account Calculator */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-16">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-blue-600 mb-4">
              <CalculatorIcon className="w-6 h-6" />
              <h2 className="text-2xl font-bold">Pricing Calculator</h2>
            </div>
            <p className="text-gray-600">
              Customize your plan based on your needs and see real-time pricing
            </p>
          </div>

          <div className="space-y-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Number of Social Media Accounts:{" "}
                <span className="font-bold text-blue-600">{accountCount}</span>
              </label>
              <div className="relative">
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={accountCount}
                  onChange={(e) => setAccountCount(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${((accountCount - 1) / 49) * 100}%, #E5E7EB ${((accountCount - 1) / 49) * 100}%, #E5E7EB 100%)`,
                  }}
                />
                <div className="flex justify-between text-sm text-gray-500 mt-2">
                  <span>1</span>
                  <span>25</span>
                  <span>50</span>
                </div>
              </div>
            </div>

            {/* Billing Period Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Billing Period
              </label>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setBillingCycle("monthly")}
                  className={`flex-1 py-2 px-4 rounded-md transition-all ${
                    billingCycle === "monthly"
                      ? "bg-white shadow text-gray-900"
                      : "text-gray-600"
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle("annual")}
                  className={`flex-1 py-2 px-4 rounded-md transition-all relative ${
                    billingCycle === "annual"
                      ? "bg-white shadow text-gray-900"
                      : "text-gray-600"
                  }`}
                >
                  Annual
                  <span className="absolute -top-2 -right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded">
                    Save 10%
                  </span>
                </button>
              </div>
            </div>

            {/* Price Display */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-6 text-white text-center">
              <div className="text-3xl md:text-4xl font-bold mb-2">
                $
                {billingCycle === "monthly"
                  ? standardMonthly.toFixed(0)
                  : standardAnnual.toFixed(0)}
                <span className="text-lg font-normal opacity-80">
                  {billingCycle === "monthly" ? "/month" : "/year"}
                </span>
              </div>

              {/* Savings Badges */}
              <div className="flex justify-center gap-2 mt-4">
                {accountCount >= 10 && (
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    60% Volume Discount
                  </span>
                )}
                {accountCount >= 5 && accountCount < 10 && (
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    40% Volume Discount
                  </span>
                )}
                {billingCycle === "annual" && (
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Save ${(standardMonthly * 12 - standardAnnual).toFixed(0)}
                    /year
                  </span>
                )}
              </div>
            </div>

            {/* Pricing Rules */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Pricing Rules:</h4>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600">
                <div>
                  <strong>Volume Discounts:</strong>
                  <br />
                  1-4 accounts: $25 each
                  <br />
                  5-9 accounts: $15 each
                  <br />
                  10+ accounts: $10 each
                </div>
                <div>
                  <strong>Annual Billing:</strong>
                  <br />
                  10% discount when paid yearly
                </div>
                <div>
                  <strong>Premium Tier:</strong>
                  <br />
                  +30% for extended features
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Comparison Table */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h2>
          <p className="text-xl text-gray-600">
            Start free and upgrade as your needs grow
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {Object.entries(tiers).map(([key, tier]) => {
            const monthlyPrice =
              key === "free" ? 0 : calculatePrice(accountCount, key);
            const annualPrice =
              key === "free" ? 0 : calculateAnnualPrice(monthlyPrice);
            const displayPrice =
              billingCycle === "annual" ? annualPrice : monthlyPrice;
            const pricePerAccount =
              accountCount > 0 ? (displayPrice / accountCount).toFixed(2) : 0;

            return (
              <div
                key={key}
                className={`relative bg-white rounded-2xl shadow-xl border-2 transition-all hover:shadow-2xl ${
                  tier.popular ? "border-blue-500 scale-105" : "border-gray-200"
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <StarIcon className="w-4 h-4" />
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="p-8">
                  <div className="text-center mb-8">
                    <div
                      className={`w-16 h-16 rounded-full bg-gradient-to-r ${key === "free" ? "from-gray-400 to-gray-600" : key === "standard" ? "from-blue-500 to-blue-700" : "from-purple-500 to-purple-700"} mx-auto mb-4 flex items-center justify-center`}
                    >
                      {key === "free" && (
                        <UsersIcon className="w-8 h-8 text-white" />
                      )}
                      {key === "standard" && (
                        <BoltIcon className="w-8 h-8 text-white" />
                      )}
                      {key === "premium" && (
                        <ShieldCheckIcon className="w-8 h-8 text-white" />
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {tier.name}
                    </h3>
                    <p className="text-gray-600 mb-4">{tier.description}</p>

                    <div className="mt-4">
                      <div className="text-center">
                        <span className="text-4xl font-bold text-gray-900">
                          ${key === "free" ? "0" : displayPrice.toFixed(0)}
                        </span>
                        <span className="text-gray-600">
                          {key === "free"
                            ? " forever"
                            : billingCycle === "monthly"
                              ? "/month"
                              : "/month (billed annually)"}
                        </span>
                      </div>

                      {key !== "free" &&
                        accountCount > 0 &&
                        billingCycle === "annual" && (
                          <div className="mt-2 text-sm text-green-600 font-medium">
                            Save ${(monthlyPrice * 12 - annualPrice).toFixed(0)}{" "}
                            annually
                          </div>
                        )}
                    </div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckIcon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleGetStarted(key)}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                      tier.popular
                        ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700"
                        : "border-2 border-gray-300 text-gray-700 hover:border-gray-400"
                    }`}
                  >
                    {tier.buttonText}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust Badges */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 mt-16">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Trusted by Growing Businesses
            </h3>
            <div className="flex justify-center items-center gap-8 text-gray-500">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="w-6 h-6" />
                <span className="font-medium">SSL Secure</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckIcon className="w-6 h-6" />
                <span className="font-medium">SOC 2 Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <StarIcon className="w-6 h-6" />
                <span className="font-medium">99.9% Uptime</span>
              </div>
            </div>
          </div>
        </div>

        {/* Volume Pricing Breakdown */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Volume Pricing Breakdown
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-lg bg-gray-50">
              <div className="text-3xl font-bold text-gray-900 mb-2">$25</div>
              <div className="text-sm text-gray-600 mb-1">
                per account/month
              </div>
              <div className="text-sm font-medium text-gray-700">
                1-4 accounts
              </div>
            </div>
            <div className="text-center p-6 rounded-lg bg-blue-50 border-2 border-blue-200">
              <div className="text-3xl font-bold text-blue-600 mb-2">$15</div>
              <div className="text-sm text-gray-600 mb-1">
                per account/month
              </div>
              <div className="text-sm font-medium text-gray-700 mb-2">
                5-9 accounts
              </div>
              <div className="inline-flex items-center bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                40% savings
              </div>
            </div>
            <div className="text-center p-6 rounded-lg bg-purple-50 border-2 border-purple-200">
              <div className="text-3xl font-bold text-purple-600 mb-2">$10</div>
              <div className="text-sm text-gray-600 mb-1">
                per account/month
              </div>
              <div className="text-sm font-medium text-gray-700 mb-2">
                10+ accounts
              </div>
              <div className="inline-flex items-center bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                60% savings
              </div>
            </div>
          </div>

          <div className="mt-8 text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-blue-800 font-medium">
              All paid plans include a <strong>14-day free trial</strong>. No
              credit card required to start.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: "Can I change my plan at any time?",
                a: "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any billing adjustments.",
              },
              {
                q: "What counts as a social media account?",
                a: "Each connected social media profile (Facebook, Instagram, Twitter, LinkedIn, etc.) counts as one account towards your limit.",
              },
              {
                q: "Do you offer refunds?",
                a: "We offer a 30-day money-back guarantee for all paid plans. If you're not satisfied, we'll provide a full refund.",
              },
              {
                q: "Is there a free trial?",
                a: "Yes! All paid plans come with a 14-day free trial. No credit card required to start.",
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept all major credit cards (Visa, MasterCard, American Express) processed securely through Stripe.",
              },
              {
                q: "Can I get volume discounts for more than 50 accounts?",
                a: "Yes! For enterprise needs with 50+ accounts, contact our sales team for custom pricing and additional features.",
              },
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow p-6">
                <h4 className="font-bold text-gray-900 mb-2">{faq.q}</h4>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses already using DZ Metrics to grow their
            social media presence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleGetStarted("standard")}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors inline-flex items-center gap-2"
            >
              Start Free Trial
              <ArrowRightIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleGetStarted("premium")}
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-medium hover:bg-white hover:text-blue-600 transition-colors"
            >
              Contact Sales
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
